import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { type HTMLMotionProps, motion } from 'framer-motion';
import * as React from 'react';
import {
  buttonVariants as motionButtonVariants,
  prefersReducedMotion,
  springSnappy,
} from '@/lib/motion';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-radius-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow hover:bg-theme-600',
        primary:
          'bg-primary text-primary-foreground shadow-md shadow-primary/20 hover:bg-theme-600 hover:shadow-primary/30',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-10 px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const shouldAnimate = !(asChild || prefersReducedMotion() || props.disabled);

    // For asChild, use regular Slot
    if (asChild) {
      return (
        <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
      );
    }

    // For regular buttons, add subtle scale on hover/tap
    return (
      <motion.button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref as React.Ref<HTMLButtonElement>}
        whileHover={shouldAnimate ? { scale: 1.02 } : undefined}
        whileTap={shouldAnimate ? { scale: 0.98 } : undefined}
        transition={springSnappy}
        {...(props as React.ComponentProps<typeof motion.button>)}
      />
    );
  },
);
Button.displayName = 'Button';

/**
 * Motion-enhanced Button with hover/tap micro-interactions.
 * Use for primary CTAs and interactive elements that benefit from tactile feedback.
 */
export interface MotionButtonProps
  extends Omit<HTMLMotionProps<'button'>, 'ref'>,
    VariantProps<typeof buttonVariants> {
  /** Disable motion animations (respects prefers-reduced-motion by default) */
  disableMotion?: boolean;
}

const MotionButton = React.forwardRef<HTMLButtonElement, MotionButtonProps>(
  ({ className, variant, size, disableMotion, disabled, ...props }, ref) => {
    const shouldAnimate = !(disableMotion || prefersReducedMotion() || disabled);

    return (
      <motion.button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled}
        variants={shouldAnimate ? motionButtonVariants : undefined}
        initial="initial"
        whileHover={shouldAnimate ? 'hover' : undefined}
        whileTap={shouldAnimate ? 'tap' : undefined}
        transition={springSnappy}
        {...props}
      />
    );
  },
);
MotionButton.displayName = 'MotionButton';

// eslint-disable-next-line react-refresh/only-export-components
export { Button, MotionButton, buttonVariants };
